import {
  getObject,
  getOptionalObject,
  getList,
  getString,
  getBoolean,
} from "@surya-digital/leo-ts-types";
import { ThemedImage, ThemedImageDTO } from "../assets/themedImage";
import { SurveyLanguage, SurveyLanguageDTO } from "./surveyLanguage";
import { SurveyLanguageCode, SurveyLanguageCodeDTO } from "./surveyLanguageCode";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
import { SurveySettingDialogProperties, SurveySettingDialogPropertiesDTO } from "./surveySettingDialogProperties";
import { MiscellaneousSettingsText, MiscellaneousSettingsTextDTO } from "./miscellaneousSettingsText";


export namespace SurveySettingsEnums {
  /* eslint-disable @typescript-eslint/no-shadow */
  export namespace FontSizeScale {
    export enum FontSizeScale {
      DEFAULT_SCALE = "DEFAULT_SCALE",
      SMALL_SCALE = "SMALL_SCALE",
      LARGE_SCALE = "LARGE_SCALE",
    }
  
    export interface FontSizeScaleDTO {
      case: string;
    }
  
    export const fromDTO = (dto: FontSizeScaleDTO): FontSizeScale => {
      if (dto === undefined || dto === null) {
        throw new InvalidSurveySettingsFontSizeScaleError(
          `Invalid DTO ${dto} provided for FontSizeScale.FontSizeScale`
        );
      }
      switch (dto.case) {
        case "DEFAULT_SCALE":
          return FontSizeScale.DEFAULT_SCALE;
        case "SMALL_SCALE":
          return FontSizeScale.SMALL_SCALE;
        case "LARGE_SCALE":
          return FontSizeScale.LARGE_SCALE;
        default:
          throw new InvalidSurveySettingsFontSizeScaleError(
            `Case ${dto.case} is not valid case of FontSizeScale.FontSizeScale`
          );
      }
    };
  
    export const toDTO = (fontSizeScale: FontSizeScale): FontSizeScaleDTO => {
      const enumDTO = {
        case: FontSizeScale[fontSizeScale],
      };
      return enumDTO;
    };
  }
  
}
export interface SurveySettingsDTO {
  clientLogo?: ThemedImageDTO | null;
  languageList: SurveyLanguageDTO[];
  selectedLanguageCode: SurveyLanguageCodeDTO;
  font: string;
  fontSizeScale: SurveySettingsEnums.FontSizeScale.FontSizeScaleDTO;
  isLanguageSelectionOptionPersistent: boolean;
  isLanguageSelectionDisplayedOnStart: boolean;
  isAutoProgressEnabled: boolean;
  isSingleSelectAnswerResetAllowed: boolean;
  isProgressPercentageShown: boolean;
  isIpTracked: boolean;
  isRespondentConsentRequired: boolean;
  isResponseResetAllowed: boolean;
  startButtonText: SurveyLocalizedTextDTO;
  genericErrorTitleText: SurveyLocalizedTextDTO;
  genericErrorDescriptionText: SurveyLocalizedTextDTO;
  continueButtonText: SurveyLocalizedTextDTO;
  nextButtonText: SurveyLocalizedTextDTO;
  backButtonText: SurveyLocalizedTextDTO;
  surveyAlreadySubmittedDialogProperties: SurveySettingDialogPropertiesDTO;
  languageSelectionDialogProperties: SurveySettingDialogPropertiesDTO;
  miscellaneousSettingsText: MiscellaneousSettingsTextDTO;
  resetAnswerButtonText: SurveyLocalizedTextDTO;
  privacyPolicyText?: SurveyLocalizedTextDTO | null;
  needAssistanceText?: SurveyLocalizedTextDTO | null;
  isRatingScaleOptionTextVisible: boolean;
  isFIThankYouPageLogoShown: boolean;
}

export class SurveySettings {
  readonly isSurveySettingsSurveySettings: boolean = true;

  readonly clientLogo: ThemedImage | null;
  readonly languageList: SurveyLanguage[];
  readonly selectedLanguageCode: SurveyLanguageCode;
  readonly font: string;
  readonly fontSizeScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
  readonly isLanguageSelectionOptionPersistent: boolean;
  readonly isLanguageSelectionDisplayedOnStart: boolean;
  readonly isAutoProgressEnabled: boolean;
  readonly isSingleSelectAnswerResetAllowed: boolean;
  readonly isProgressPercentageShown: boolean;
  readonly isIpTracked: boolean;
  readonly isRespondentConsentRequired: boolean;
  readonly isResponseResetAllowed: boolean;
  readonly startButtonText: SurveyLocalizedText;
  readonly genericErrorTitleText: SurveyLocalizedText;
  readonly genericErrorDescriptionText: SurveyLocalizedText;
  readonly continueButtonText: SurveyLocalizedText;
  readonly nextButtonText: SurveyLocalizedText;
  readonly backButtonText: SurveyLocalizedText;
  readonly surveyAlreadySubmittedDialogProperties: SurveySettingDialogProperties;
  readonly languageSelectionDialogProperties: SurveySettingDialogProperties;
  readonly miscellaneousSettingsText: MiscellaneousSettingsText;
  readonly resetAnswerButtonText: SurveyLocalizedText;
  readonly privacyPolicyText: SurveyLocalizedText | null;
  readonly needAssistanceText: SurveyLocalizedText | null;
  readonly isRatingScaleOptionTextVisible: boolean;
  readonly isFIThankYouPageLogoShown: boolean;

  static readonly constraints = {
    "font": {
      "maxSize": 64,
      
      
      
      
    },
  }

  constructor(
  clientLogo: ThemedImage | null = null, 
  languageList: SurveyLanguage[], 
  selectedLanguageCode: SurveyLanguageCode, 
  font: string, 
  fontSizeScale: SurveySettingsEnums.FontSizeScale.FontSizeScale, 
  isLanguageSelectionOptionPersistent: boolean, 
  isLanguageSelectionDisplayedOnStart: boolean, 
  isAutoProgressEnabled: boolean, 
  isSingleSelectAnswerResetAllowed: boolean, 
  isProgressPercentageShown: boolean, 
  isIpTracked: boolean, 
  isRespondentConsentRequired: boolean, 
  isResponseResetAllowed: boolean, 
  startButtonText: SurveyLocalizedText, 
  genericErrorTitleText: SurveyLocalizedText, 
  genericErrorDescriptionText: SurveyLocalizedText, 
  continueButtonText: SurveyLocalizedText, 
  nextButtonText: SurveyLocalizedText, 
  backButtonText: SurveyLocalizedText, 
  surveyAlreadySubmittedDialogProperties: SurveySettingDialogProperties, 
  languageSelectionDialogProperties: SurveySettingDialogProperties, 
  miscellaneousSettingsText: MiscellaneousSettingsText, 
  resetAnswerButtonText: SurveyLocalizedText, 
  privacyPolicyText: SurveyLocalizedText | null = null, 
  needAssistanceText: SurveyLocalizedText | null = null, 
  isRatingScaleOptionTextVisible: boolean, 
  isFIThankYouPageLogoShown: boolean
  ) {
    this.clientLogo = clientLogo;
    this.languageList = languageList;
    this.selectedLanguageCode = selectedLanguageCode;
    SurveySettings.validateFont(font);
    this.font = font;
    this.fontSizeScale = fontSizeScale;
    this.isLanguageSelectionOptionPersistent = isLanguageSelectionOptionPersistent;
    this.isLanguageSelectionDisplayedOnStart = isLanguageSelectionDisplayedOnStart;
    this.isAutoProgressEnabled = isAutoProgressEnabled;
    this.isSingleSelectAnswerResetAllowed = isSingleSelectAnswerResetAllowed;
    this.isProgressPercentageShown = isProgressPercentageShown;
    this.isIpTracked = isIpTracked;
    this.isRespondentConsentRequired = isRespondentConsentRequired;
    this.isResponseResetAllowed = isResponseResetAllowed;
    this.startButtonText = startButtonText;
    this.genericErrorTitleText = genericErrorTitleText;
    this.genericErrorDescriptionText = genericErrorDescriptionText;
    this.continueButtonText = continueButtonText;
    this.nextButtonText = nextButtonText;
    this.backButtonText = backButtonText;
    this.surveyAlreadySubmittedDialogProperties = surveyAlreadySubmittedDialogProperties;
    this.languageSelectionDialogProperties = languageSelectionDialogProperties;
    this.miscellaneousSettingsText = miscellaneousSettingsText;
    this.resetAnswerButtonText = resetAnswerButtonText;
    this.privacyPolicyText = privacyPolicyText;
    this.needAssistanceText = needAssistanceText;
    this.isRatingScaleOptionTextVisible = isRatingScaleOptionTextVisible;
    this.isFIThankYouPageLogoShown = isFIThankYouPageLogoShown;
  }

  static fromDTO(dto: SurveySettingsDTO): SurveySettings {
    let clientLogo = getOptionalObject(dto, "clientLogo");
    if(clientLogo !== undefined && clientLogo !== null) {
      clientLogo = ThemedImage.fromDTO(getObject(dto, "clientLogo", InvalidSurveySettingsClientLogoError));
    }
    const languageList = getList<SurveyLanguageDTO, SurveyLanguage>(
      dto,
      "languageList",
      SurveyLanguage.fromDTO,
      InvalidSurveySettingsLanguageListError
    );
    const selectedLanguageCode = SurveyLanguageCode.fromDTO(getObject(dto, "selectedLanguageCode", InvalidSurveySettingsSelectedLanguageCodeError));
    const font = getString(dto, "font", InvalidSurveySettingsFontError);
    const fontSizeScale = SurveySettingsEnums.FontSizeScale.fromDTO(getObject(dto, "fontSizeScale", InvalidSurveySettingsFontSizeScaleError));
    const isLanguageSelectionOptionPersistent = getBoolean(dto, "isLanguageSelectionOptionPersistent", InvalidSurveySettingsIsLanguageSelectionOptionPersistentError);
    const isLanguageSelectionDisplayedOnStart = getBoolean(dto, "isLanguageSelectionDisplayedOnStart", InvalidSurveySettingsIsLanguageSelectionDisplayedOnStartError);
    const isAutoProgressEnabled = getBoolean(dto, "isAutoProgressEnabled", InvalidSurveySettingsIsAutoProgressEnabledError);
    const isSingleSelectAnswerResetAllowed = getBoolean(dto, "isSingleSelectAnswerResetAllowed", InvalidSurveySettingsIsSingleSelectAnswerResetAllowedError);
    const isProgressPercentageShown = getBoolean(dto, "isProgressPercentageShown", InvalidSurveySettingsIsProgressPercentageShownError);
    const isIpTracked = getBoolean(dto, "isIpTracked", InvalidSurveySettingsIsIpTrackedError);
    const isRespondentConsentRequired = getBoolean(dto, "isRespondentConsentRequired", InvalidSurveySettingsIsRespondentConsentRequiredError);
    const isResponseResetAllowed = getBoolean(dto, "isResponseResetAllowed", InvalidSurveySettingsIsResponseResetAllowedError);
    const startButtonText = SurveyLocalizedText.fromDTO(getObject(dto, "startButtonText", InvalidSurveySettingsStartButtonTextError));
    const genericErrorTitleText = SurveyLocalizedText.fromDTO(getObject(dto, "genericErrorTitleText", InvalidSurveySettingsGenericErrorTitleTextError));
    const genericErrorDescriptionText = SurveyLocalizedText.fromDTO(getObject(dto, "genericErrorDescriptionText", InvalidSurveySettingsGenericErrorDescriptionTextError));
    const continueButtonText = SurveyLocalizedText.fromDTO(getObject(dto, "continueButtonText", InvalidSurveySettingsContinueButtonTextError));
    const nextButtonText = SurveyLocalizedText.fromDTO(getObject(dto, "nextButtonText", InvalidSurveySettingsNextButtonTextError));
    const backButtonText = SurveyLocalizedText.fromDTO(getObject(dto, "backButtonText", InvalidSurveySettingsBackButtonTextError));
    const surveyAlreadySubmittedDialogProperties = SurveySettingDialogProperties.fromDTO(getObject(dto, "surveyAlreadySubmittedDialogProperties", InvalidSurveySettingsSurveyAlreadySubmittedDialogPropertiesError));
    const languageSelectionDialogProperties = SurveySettingDialogProperties.fromDTO(getObject(dto, "languageSelectionDialogProperties", InvalidSurveySettingsLanguageSelectionDialogPropertiesError));
    const miscellaneousSettingsText = MiscellaneousSettingsText.fromDTO(getObject(dto, "miscellaneousSettingsText", InvalidSurveySettingsMiscellaneousSettingsTextError));
    const resetAnswerButtonText = SurveyLocalizedText.fromDTO(getObject(dto, "resetAnswerButtonText", InvalidSurveySettingsResetAnswerButtonTextError));
    let privacyPolicyText = getOptionalObject(dto, "privacyPolicyText");
    if(privacyPolicyText !== undefined && privacyPolicyText !== null) {
      privacyPolicyText = SurveyLocalizedText.fromDTO(getObject(dto, "privacyPolicyText", InvalidSurveySettingsPrivacyPolicyTextError));
    }
    let needAssistanceText = getOptionalObject(dto, "needAssistanceText");
    if(needAssistanceText !== undefined && needAssistanceText !== null) {
      needAssistanceText = SurveyLocalizedText.fromDTO(getObject(dto, "needAssistanceText", InvalidSurveySettingsNeedAssistanceTextError));
    }
    const isRatingScaleOptionTextVisible = getBoolean(dto, "isRatingScaleOptionTextVisible", InvalidSurveySettingsIsRatingScaleOptionTextVisibleError);
    const isFIThankYouPageLogoShown = getBoolean(dto, "isFIThankYouPageLogoShown", InvalidSurveySettingsIsFiThankYouPageLogoShownError);
    return new SurveySettings(
      clientLogo,
      languageList,
      selectedLanguageCode,
      font,
      fontSizeScale,
      isLanguageSelectionOptionPersistent,
      isLanguageSelectionDisplayedOnStart,
      isAutoProgressEnabled,
      isSingleSelectAnswerResetAllowed,
      isProgressPercentageShown,
      isIpTracked,
      isRespondentConsentRequired,
      isResponseResetAllowed,
      startButtonText,
      genericErrorTitleText,
      genericErrorDescriptionText,
      continueButtonText,
      nextButtonText,
      backButtonText,
      surveyAlreadySubmittedDialogProperties,
      languageSelectionDialogProperties,
      miscellaneousSettingsText,
      resetAnswerButtonText,
      privacyPolicyText,
      needAssistanceText,
      isRatingScaleOptionTextVisible,
      isFIThankYouPageLogoShown
    );
  }

  toDTO(): SurveySettingsDTO {
    const dto: SurveySettingsDTO = {
        languageList: this.languageList.map((e: SurveyLanguage) => { return e.toDTO() }),
        selectedLanguageCode: this.selectedLanguageCode.toDTO(),
        font: this.font,
        fontSizeScale: SurveySettingsEnums.FontSizeScale.toDTO(this.fontSizeScale),
        isLanguageSelectionOptionPersistent: this.isLanguageSelectionOptionPersistent,
        isLanguageSelectionDisplayedOnStart: this.isLanguageSelectionDisplayedOnStart,
        isAutoProgressEnabled: this.isAutoProgressEnabled,
        isSingleSelectAnswerResetAllowed: this.isSingleSelectAnswerResetAllowed,
        isProgressPercentageShown: this.isProgressPercentageShown,
        isIpTracked: this.isIpTracked,
        isRespondentConsentRequired: this.isRespondentConsentRequired,
        isResponseResetAllowed: this.isResponseResetAllowed,
        startButtonText: this.startButtonText.toDTO(),
        genericErrorTitleText: this.genericErrorTitleText.toDTO(),
        genericErrorDescriptionText: this.genericErrorDescriptionText.toDTO(),
        continueButtonText: this.continueButtonText.toDTO(),
        nextButtonText: this.nextButtonText.toDTO(),
        backButtonText: this.backButtonText.toDTO(),
        surveyAlreadySubmittedDialogProperties: this.surveyAlreadySubmittedDialogProperties.toDTO(),
        languageSelectionDialogProperties: this.languageSelectionDialogProperties.toDTO(),
        miscellaneousSettingsText: this.miscellaneousSettingsText.toDTO(),
        resetAnswerButtonText: this.resetAnswerButtonText.toDTO(),
        isRatingScaleOptionTextVisible: this.isRatingScaleOptionTextVisible,
        isFIThankYouPageLogoShown: this.isFIThankYouPageLogoShown
    };
    if (this.clientLogo !== undefined && this.clientLogo !== null){
      dto.clientLogo = this.clientLogo.toDTO();
    }
    if (this.privacyPolicyText !== undefined && this.privacyPolicyText !== null){
      dto.privacyPolicyText = this.privacyPolicyText.toDTO();
    }
    if (this.needAssistanceText !== undefined && this.needAssistanceText !== null){
      dto.needAssistanceText = this.needAssistanceText.toDTO();
    }
    return dto;
  }
  private static validateFont = (font: string): void => {
    if(font.length > 64) {
      throw new InvalidSurveySettingsFontMaxSizeError(
        64,
        `Attribute font is too long. Size is ${font.length}. Max size is 64.`
      );
    }
      if(font.trim().length === 0) {
        throw new InvalidSurveySettingsFontNonEmptyError(
          `Attribute font cannot be an empty string.`
        );
      }
  }
  
}

SurveySettings.prototype.toString = function toString(): string {
  return `SurveySettings(clientLogo='${this.clientLogo}',languageList='${this.languageList}',selectedLanguageCode='${this.selectedLanguageCode}',font='${this.font}',fontSizeScale='${this.fontSizeScale}',isLanguageSelectionOptionPersistent='${this.isLanguageSelectionOptionPersistent}',isLanguageSelectionDisplayedOnStart='${this.isLanguageSelectionDisplayedOnStart}',isAutoProgressEnabled='${this.isAutoProgressEnabled}',isSingleSelectAnswerResetAllowed='${this.isSingleSelectAnswerResetAllowed}',isProgressPercentageShown='${this.isProgressPercentageShown}',isIpTracked='${this.isIpTracked}',isRespondentConsentRequired='${this.isRespondentConsentRequired}',isResponseResetAllowed='${this.isResponseResetAllowed}',startButtonText='${this.startButtonText}',genericErrorTitleText='${this.genericErrorTitleText}',genericErrorDescriptionText='${this.genericErrorDescriptionText}',continueButtonText='${this.continueButtonText}',nextButtonText='${this.nextButtonText}',backButtonText='${this.backButtonText}',surveyAlreadySubmittedDialogProperties='${this.surveyAlreadySubmittedDialogProperties}',languageSelectionDialogProperties='${this.languageSelectionDialogProperties}',miscellaneousSettingsText='${this.miscellaneousSettingsText}',resetAnswerButtonText='${this.resetAnswerButtonText}',privacyPolicyText='${this.privacyPolicyText}',needAssistanceText='${this.needAssistanceText}',isRatingScaleOptionTextVisible='${this.isRatingScaleOptionTextVisible}',isFIThankYouPageLogoShown='${this.isFIThankYouPageLogoShown}')`;
};

export class InvalidSurveySettingsError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsError";
  }
}
export class InvalidSurveySettingsClientLogoError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsClientLogoError";
  }
}

export class InvalidSurveySettingsLanguageListError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsLanguageListError";
  }
}

export class InvalidSurveySettingsSelectedLanguageCodeError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsSelectedLanguageCodeError";
  }
}

export class InvalidSurveySettingsFontError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsFontError";
  }
}

export class InvalidSurveySettingsFontMaxSizeError extends InvalidSurveySettingsFontError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsFontMaxSizeError";
  }
}

export class InvalidSurveySettingsFontNonEmptyError extends InvalidSurveySettingsFontError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsFontNonEmptyError";
  }
}

export class InvalidSurveySettingsFontSizeScaleError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsFontSizeScaleError";
  }
}

export class InvalidSurveySettingsFontSizeScaleDefaultScaleError extends InvalidSurveySettingsFontSizeScaleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsFontSizeScaleDefaultScaleError";
  }
}

export class InvalidSurveySettingsFontSizeScaleSmallScaleError extends InvalidSurveySettingsFontSizeScaleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsFontSizeScaleSmallScaleError";
  }
}

export class InvalidSurveySettingsFontSizeScaleLargeScaleError extends InvalidSurveySettingsFontSizeScaleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsFontSizeScaleLargeScaleError";
  }
}

export class InvalidSurveySettingsIsLanguageSelectionOptionPersistentError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsIsLanguageSelectionOptionPersistentError";
  }
}

export class InvalidSurveySettingsIsLanguageSelectionDisplayedOnStartError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsIsLanguageSelectionDisplayedOnStartError";
  }
}

export class InvalidSurveySettingsIsAutoProgressEnabledError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsIsAutoProgressEnabledError";
  }
}

export class InvalidSurveySettingsIsSingleSelectAnswerResetAllowedError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsIsSingleSelectAnswerResetAllowedError";
  }
}

export class InvalidSurveySettingsIsProgressPercentageShownError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsIsProgressPercentageShownError";
  }
}

export class InvalidSurveySettingsIsIpTrackedError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsIsIpTrackedError";
  }
}

export class InvalidSurveySettingsIsRespondentConsentRequiredError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsIsRespondentConsentRequiredError";
  }
}

export class InvalidSurveySettingsIsResponseResetAllowedError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsIsResponseResetAllowedError";
  }
}

export class InvalidSurveySettingsStartButtonTextError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsStartButtonTextError";
  }
}

export class InvalidSurveySettingsGenericErrorTitleTextError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsGenericErrorTitleTextError";
  }
}

export class InvalidSurveySettingsGenericErrorDescriptionTextError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsGenericErrorDescriptionTextError";
  }
}

export class InvalidSurveySettingsContinueButtonTextError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsContinueButtonTextError";
  }
}

export class InvalidSurveySettingsNextButtonTextError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsNextButtonTextError";
  }
}

export class InvalidSurveySettingsBackButtonTextError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsBackButtonTextError";
  }
}

export class InvalidSurveySettingsSurveyAlreadySubmittedDialogPropertiesError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsSurveyAlreadySubmittedDialogPropertiesError";
  }
}

export class InvalidSurveySettingsLanguageSelectionDialogPropertiesError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsLanguageSelectionDialogPropertiesError";
  }
}

export class InvalidSurveySettingsMiscellaneousSettingsTextError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsMiscellaneousSettingsTextError";
  }
}

export class InvalidSurveySettingsResetAnswerButtonTextError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsResetAnswerButtonTextError";
  }
}

export class InvalidSurveySettingsPrivacyPolicyTextError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsPrivacyPolicyTextError";
  }
}

export class InvalidSurveySettingsNeedAssistanceTextError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsNeedAssistanceTextError";
  }
}

export class InvalidSurveySettingsIsRatingScaleOptionTextVisibleError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsIsRatingScaleOptionTextVisibleError";
  }
}

export class InvalidSurveySettingsIsFiThankYouPageLogoShownError extends InvalidSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveySettingsIsFiThankYouPageLogoShownError";
  }
}
